import React, { useState } from 'react';
import { SelectTabData, SelectTabEvent, Tab, TabList, mergeClasses, useId } from '@fluentui/react-components';
import './GuideComponents.css';

export interface GuideTabItem {
  /** Identificador estable de la solapa (también es el `value` del Tab de Fluent). */
  key: string;
  label: React.ReactNode;
  /** Badge a la derecha del texto (p. ej. "PREVIEW", en alerta tenue). */
  badge?: React.ReactNode;
  /** Contenido; se monta sólo mientras la solapa está activa (igual que el Pivot de v8). */
  content: React.ReactNode;
  disabled?: boolean;
}

export interface GuideTabsProps {
  /** Solapas en orden. Las que no corresponden (feature flags) se filtran antes o se pasan como `false`. */
  tabs: Array<GuideTabItem | false | null | undefined>;
  /** Nombre accesible de la lista de solapas. */
  ariaLabel: string;
  /** Solapa activa (controlado). */
  selectedKey?: string;
  /** Solapa activa inicial (no controlado). Por defecto, la primera. */
  defaultSelectedKey?: string;
  onTabChange?: (key: string) => void;
  /** Mantiene montadas las solapas ya visitadas (ocultas). Por defecto se desmontan, como el Pivot. */
  keepMounted?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Solapas subrayadas de la guía ("Solapas"): secciones dentro de una misma entidad; la activa en
 * primario con subrayado primario, las demás en texto secundario, línea divisoria debajo y badge
 * opcional (p. ej. "PREVIEW" en alerta tenue). Sobre el `TabList` de Fluent v9 (teclado con flechas,
 * roles tab / tabpanel). El contenido es diferido: sólo se renderiza la solapa activa.
 */
export const GuideTabs = ({
  tabs,
  ariaLabel,
  selectedKey,
  defaultSelectedKey,
  onTabChange,
  keepMounted = false,
  className,
  'data-testid': testId,
}: GuideTabsProps) => {
  const baseId = useId('guide-tabs');
  const items = tabs.filter((tab): tab is GuideTabItem => !!tab);
  const [internalKey, setInternalKey] = useState<string | undefined>(defaultSelectedKey);
  const [visited, setVisited] = useState<string[]>([]);

  const requested = selectedKey ?? internalKey;
  const activeKey = items.some((tab) => tab.key === requested) ? (requested as string) : items[0]?.key;

  const onSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    const key = String(data.value);
    if (selectedKey === undefined) setInternalKey(key);
    if (keepMounted && activeKey && !visited.includes(activeKey)) setVisited((current) => [...current, activeKey]);
    onTabChange?.(key);
  };

  const tabId = (key: string) => `${baseId}-tab-${key}`;
  const panelId = (key: string) => `${baseId}-panel-${key}`;

  const renderPanel = (tab: GuideTabItem) => {
    const active = tab.key === activeKey;
    if (!active && !(keepMounted && visited.includes(tab.key))) return null;
    return (
      <div
        key={tab.key}
        role="tabpanel"
        id={panelId(tab.key)}
        aria-labelledby={tabId(tab.key)}
        className="ichtys-tabs__panel"
        hidden={!active}
        tabIndex={0}
        data-tab-panel={tab.key}
      >
        {tab.content}
      </div>
    );
  };

  return (
    <div className={mergeClasses('ichtys-tabs-root', className)} data-testid={testId}>
      <TabList
        className="ichtys-tabs"
        size="large"
        appearance="transparent"
        selectedValue={activeKey}
        onTabSelect={onSelect}
        aria-label={ariaLabel}
      >
        {items.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            id={tabId(tab.key)}
            aria-controls={tab.key === activeKey ? panelId(tab.key) : undefined}
            disabled={tab.disabled}
            className="ichtys-tab"
          >
            <span className="ichtys-tab__label">
              {tab.label}
              {tab.badge && <span className="ichtys-tab__badge">{tab.badge}</span>}
            </span>
          </Tab>
        ))}
      </TabList>
      {items.map(renderPanel)}
    </div>
  );
};

export default GuideTabs;
